import {
  Component,
  OnInit,
  Input,
  ElementRef,
  Output,
  EventEmitter,
  ViewChild,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { fromEvent, Subscription } from 'rxjs';

@Component({
  selector: 'app-select-box',
  templateUrl: './select-box.component.html',
  styleUrls: ['./select-box.component.scss'],
  standalone: false,
})
export class SelectBoxComponent implements OnInit, OnChanges, OnDestroy {
  isOpen: boolean = false;
  isInvalid: boolean = false;
  styles: any = {
    wrapper: '',
  };
  searchText: string = '';
  selectedOptionLabel: string = '';
  private documentClickSubscription!: Subscription;

  @ViewChild('searchInput') searchInput: any;
  @Input() modelValue: any;
  @Input() cssStyles: any;
  @Input() options: any;
  @Input() inputName!: string;
  @Input() label!: string;
  @Input() isRequired: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() search: boolean = false;
  @Input() searchPlaceholder!: string;
  @Input() loading!: boolean;
  @Input() form: UntypedFormGroup | null = null;
  @Input() optionLabelKey: string = 'name';
  @Input() isSelectedFromOptions: boolean = false;
  @Input() inputDescription!: string;
  @Input() disabled: boolean = false;
  @Input() showLabelWhenSelected: boolean = true;
  @Input() margin: boolean = true;
  @Output() selected: EventEmitter<any> = new EventEmitter<any>();

  private readonly select: ElementRef = inject(ElementRef);

  ngOnInit(): void {
    this.isOpen = false;

    // Use fromEvent to listen for clicks outside the component
    this.documentClickSubscription = fromEvent<MouseEvent>(document, 'click').subscribe((event: MouseEvent) => {
      if (!this.select.nativeElement.contains(event.target)) {
        this.isOpen = false;
        this.searchText = '';
      }
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe from the observable to prevent memory leaks
    if (this.documentClickSubscription) {
      this.documentClickSubscription.unsubscribe();
      this.documentClickSubscription = undefined!;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.modelValue?.currentValue || changes?.modelValue?.currentValue === ''
      || changes?.options?.currentValue) {
      this.selectedOptionLabel = this.getSelectedOptionLabel();
    }
  }

  showOptions(event: Event): void {
    const target: Element = event.target as Element;
    if (this.disabled) {
      this.isOpen = false;
    } else if (this.searchInput?.nativeElement.contains(target) || target.classList.contains('disabled')) {
      this.isOpen = true;
    } else {
      this.isOpen = !this.isOpen;
      this.searchText = '';
    }
  }

  selectOption(option: any): void {
    if (option?.disabled) {
      return;
    }
    const { name, value, ...additional } = option;
    const selected: any = this.options.find((entity: any) => entity.selected);
    this.selected.emit({
      value,
      key: this.inputName,
      name,
      ...additional,
      inputDescription: this.inputDescription,
      oldValue: selected,
      inputType: 'selectBox',
    });
  }

  isActive(): boolean {
    if (!!this.modelValue && typeof this.modelValue === 'object') {
      return Object.keys(this.modelValue).length !== 0;
    }
    return !!this.modelValue;
  }

  showPlaceholder(): boolean {
    if (!this.showLabelWhenSelected) {
      return !this.isActive();
    }

    return !!this.label;
  }

  private getSelectedOptionLabel(): string {
    if (this.isSelectedFromOptions) {
      const selected: any = this.options.find((entity: any) => entity.selected);
      return selected ? selected.description : '';
    }
    if (!this.modelValue) {
      return '';
    }

    if (typeof this.modelValue === 'object') {
      return this.getLabelForObjectModel();
    }
    const option: any = this.options.find((opt: any) => opt.value === this.modelValue);
    return option ? option.name : '';
  }

  private getLabelForObjectModel(): string {
    if (Object.keys(this.modelValue).length === 0) {
      return '';
    }

    if (this.modelValue?.name) {
      return this.modelValue.name;
    }

    return Object.keys(this.modelValue).map(key =>
      (typeof this.modelValue[key] === 'string'
        && this.modelValue[key] !== 'empty')
        ? this.modelValue[key]
        : '')
      .join(' ');
  }

  getLabel(): string {
    return `${this.label}${this.isRequired ? '*' : ''}`;
  }

  updateSearch($event: any): void {
    this.searchText = $event.target.value;
  }

  onClick(event: Event): void {
    if (!this.select.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.searchText = '';
    }
  }
}
